import { create } from 'zustand';
import { createFurniture, createProject } from '../projects/project';
import { furnitureCoordinates, insertionPosition, positionAtCoordinates } from '../projects/placement';
import type { CameraView, DrawingEntity, DrawingKind, EditorMode, FurnitureKind, FurnitureObject, Language, Material, PanelMaterialOverride, Project, Room, Vec3 } from '../types';
import { linePlanPoints, validLine } from '../geometry/line3d';
import { GRID_SNAP_MM } from '../geometry/grid';
import { scaleFurniture } from '../geometry/scaleFurniture';
import { mirrorDrawing, offsetLine } from '../geometry/drawingTools';
import { closedFaceFromLines, faceBasis } from '../geometry/faces';
import { pushPullFaceSurface, type FaceSurface } from '../geometry/pushPullSurface';
import { roomWithPatch } from '../geometry/roomOutline';
import i18n from '../i18n';
import { createId } from '../utils/id';

type SaveStatus = 'saved' | 'saving' | 'offline' | 'unsaved';

interface Command {
  label: string;
  execute(project: Project): Project;
  undo(project: Project): Project;
}

const withUpdated = (project: Project, patch: Partial<Project>): Project => ({ ...project, ...patch, updatedAt: new Date().toISOString() });

class ProjectMutationCommand implements Command {
  constructor(public label: string, private before: Project, private after: Project) {}
  execute(_project: Project) { return structuredClone(this.after); }
  undo(_project: Project) { return structuredClone(this.before); }
}

interface EditorState {
  project: Project;
  selectedId: string | null;
  selectedDrawingId: string | null;
  selectedIds: string[];
  selectedDrawingIds: string[];
  selectedGroupId: string | null;
  mode: EditorMode;
  cameraView: CameraView;
  saveStatus: SaveStatus;
  history: Command[];
  future: Command[];
  focusNonce: number;
  transformMode: 'select' | 'translate' | 'rotate' | 'scale';
  selectedPanelId: string | null;
  activeTool: 'select' | 'boxSelect' | 'eraser' | 'pan' | 'orbit' | 'zoom' | 'line' | 'polyline' | 'rectangle' | 'circle' | 'arc' | 'polygon' | 'freehand' | 'move' | 'rotate' | 'scale' | 'mirror' | 'offset' | 'measure' | 'dimension' | 'pushPull';
  fullscreenViewport: boolean;
  setProject: (project: Project) => void;
  newProject: (name: string, language: Language) => void;
  renameProject: (name: string) => void;
  setProjectLocation: (latitude: number, longitude: number) => void;
  addFurniture: (kind: FurnitureKind) => void;
  addConfiguredFurniture: (item: FurnitureObject) => void;
  updateFurniture: (id: string, patch: Partial<FurnitureObject>, label?: string) => void;
  scaleSelected: (factors: Vec3) => boolean;
  pushPullSelected: (axis: 0 | 1 | 2, distanceMm: number) => boolean;
  pushPullDrawingFace: (drawingId: string, surface: FaceSurface, distanceMm: number) => boolean;
  moveSelection: (delta: Vec3) => boolean;
  mirrorSelected: (axis: 'x' | 'z') => boolean;
  offsetSelectedLine: (distanceMm: number) => boolean;
  deleteSelected: () => void;
  addDrawing: (kind: DrawingKind, points: Array<[number, number]>) => void;
  addPolyline3D: (vertices: Vec3[]) => void;
  addLine3D: (start: Vec3, end: Vec3) => void;
  updateLine3D: (id: string, start: Vec3, end: Vec3) => void;
  selectDrawing: (id: string | null) => void;
  selectMany: (furnitureIds: string[], drawingIds: string[]) => void;
  selectGroup: (id: string | null) => void;
  groupFurniture: (ids: string[], name: string) => boolean;
  ungroupSelected: () => void;
  updateRoom: (patch: Partial<Room>) => void;
  deleteRoom: () => void;
  restoreRoom: () => void;
  updateOrigin: (origin: Vec3) => void;
  select: (id: string | null) => void;
  selectPanel: (furnitureId: string, panelId: string) => void;
  setMode: (mode: EditorMode) => void;
  setCameraView: (view: CameraView) => void;
  toggleGrid: () => void;
  toggleSnap: () => void;
  toggleOrtho: () => void;
  toggleVisibility: (id: string) => void;
  nudgeSelected: (axis: 0 | 1 | 2, delta: number) => void;
  undo: () => void;
  redo: () => void;
  focusSelected: () => void;
  setTransformMode: (mode: 'select' | 'translate' | 'rotate') => void;
  setActiveTool: (tool: EditorState['activeTool']) => void;
  setFullscreenViewport: (value: boolean) => void;
  duplicateSelected: () => void;
  setPanelMaterial: (furnitureId: string, panelId: string, override: PanelMaterialOverride) => void;
  addMaterial: (material: Material) => void;
  markSaving: () => void;
  markSaved: (project?: Project) => void;
  markOffline: () => void;
}

const initialLanguage = ((localStorage.getItem('arborcad-language') || navigator.language.slice(0, 2)) as Language);
const initialProject = createProject(i18n.t('project.untitled'), ['hr', 'en', 'de'].includes(initialLanguage) ? initialLanguage : 'en');

const commit = (set: (partial: Partial<EditorState>) => void, get: () => EditorState, label: string, mutate: (p: Project) => Project) => {
  const before = structuredClone(get().project);
  const after = withUpdated(mutate(structuredClone(before)), {});
  const command = new ProjectMutationCommand(label, before, after);
  set({ project: command.execute(before), history: [...get().history, command].slice(-100), future: [], saveStatus: 'unsaved' });
};

export const useEditorStore = create<EditorState>((set, get) => ({
  project: initialProject, selectedId: null, selectedDrawingId: null, selectedIds: [], selectedDrawingIds: [], selectedGroupId: null, selectedPanelId: null, mode: '3d', cameraView: 'perspective', saveStatus: 'unsaved', history: [], future: [], focusNonce: 0, transformMode: 'select', activeTool: 'select', fullscreenViewport: false,
  setProject: (project) => set({ project: { ...project, groups: project.groups ?? [], settings: { ...project.settings, gridSize: GRID_SNAP_MM } }, selectedId: null, selectedDrawingId: null, selectedIds: [], selectedDrawingIds: [], selectedGroupId: null, selectedPanelId: null, history: [], future: [], saveStatus: 'saved' }),
  newProject: (name, language) => set({ project: createProject(name, language), selectedId: null, selectedDrawingId: null, selectedIds: [], selectedDrawingIds: [], selectedGroupId: null, selectedPanelId: null, history: [], future: [], saveStatus: 'unsaved' }),
  renameProject: (name) => commit(set, get, 'Rename project', (project) => ({ ...project, name })),
  setProjectLocation: (latitude, longitude) => commit(set, get, 'Set project location', (project) => ({ ...project, location: { latitude, longitude } })),
  addFurniture: (kind) => {
    const item = createFurniture(kind);
    const baseName = i18n.t(kind === 'wardrobe' ? 'library.wardrobe' : 'library.baseCabinet');
    item.name = `${baseName} ${get().project.furniture.filter((f) => f.kind === kind).length + 1}`;
    item.position = insertionPosition(get().project, item);
    commit(set, get, `Add ${kind}`, (project) => ({ ...project, furniture: [...project.furniture, item] }));
    set({ selectedId: item.id, selectedDrawingId: null, selectedIds: [item.id], selectedDrawingIds: [], selectedGroupId: null, focusNonce: get().focusNonce + 1 });
  },
  addConfiguredFurniture: (configured) => {
    const item = structuredClone(configured);
    item.id = createId(item.kind);
    item.position = insertionPosition(get().project, item);
    commit(set, get, 'Add configured wardrobe', (project) => ({ ...project, furniture: [...project.furniture, item] }));
    set({ selectedId: item.id, selectedDrawingId: null, selectedIds: [item.id], selectedDrawingIds: [], selectedGroupId: null, selectedPanelId: null, activeTool: 'select', transformMode: 'select', focusNonce: get().focusNonce + 1 });
  },
  updateFurniture: (id, patch, label = 'Edit object') => commit(set, get, label, (project) => {
    const primary = project.furniture.find((item) => item.id === id);
    const groupId = get().selectedGroupId && primary?.groupId === get().selectedGroupId ? get().selectedGroupId : null;
    const delta: Vec3 = primary && patch.position ? [patch.position[0] - primary.position[0], patch.position[1] - primary.position[1], patch.position[2] - primary.position[2]] : [0, 0, 0];
    const angle = primary && patch.rotation ? patch.rotation[1] - primary.rotation[1] : 0;
    return { ...project, furniture: project.furniture.map((item) => {
      if (item.id !== id && item.groupId !== groupId) return item;
      if (item.id !== id) {
        const dx = item.position[0] - primary!.position[0], dz = item.position[2] - primary!.position[2];
        const position: Vec3 = [primary!.position[0] + dx * Math.cos(angle) - dz * Math.sin(angle) + delta[0], item.position[1] + delta[1], primary!.position[2] + dx * Math.sin(angle) + dz * Math.cos(angle) + delta[2]];
        return { ...item, position, rotation: angle ? [item.rotation[0], item.rotation[1] + angle, item.rotation[2]] as Vec3 : item.rotation };
      }
      const next = { ...item, ...patch };
      if (!groupId && patch.position === undefined && (patch.width !== undefined || patch.depth !== undefined || patch.rotation !== undefined)) {
        next.position = positionAtCoordinates(next, project.origin, furnitureCoordinates(item, project.origin));
      }
      return next;
    }) };
  }),
  scaleSelected: (factors) => {
    const item = get().project.furniture.find((entry) => entry.id === get().selectedId);
    if (!item) return false;
    if (get().selectedGroupId && item.groupId === get().selectedGroupId) {
      const members = get().project.furniture.filter((entry) => entry.groupId === item.groupId);
      const scaled = members.map((member) => scaleFurniture(member, factors));
      if (scaled.some((entry) => !entry)) return false;
      const replacements = new Map(members.map((member, index) => [member.id, {
        ...scaled[index]!,
        position: member.position.map((value, axis) => item.position[axis] + (value - item.position[axis]) * factors[axis]) as Vec3,
      }]));
      commit(set, get, 'Scale group', (project) => ({ ...project, furniture: project.furniture.map((entry) => replacements.get(entry.id) ?? entry) }));
      return true;
    }
    const scaled = scaleFurniture(item, factors);
    if (!scaled) return false;
    get().updateFurniture(item.id, scaled, 'Scale object');
    return true;
  },
  pushPullSelected: (axis, distanceMm) => {
    const item = get().project.furniture.find((entry) => entry.id === get().selectedId);
    if (item) {
      if (!Number.isFinite(distanceMm) || distanceMm === 0) return false;
      const current = [item.width, item.height, item.depth][axis];
      const factors: Vec3 = [1, 1, 1]; factors[axis] = (current + distanceMm) / current;
      return get().scaleSelected(factors);
    }
    const face = get().project.drawings.find((entry) => entry.id === get().selectedDrawingId && entry.kind === 'face');
    if (face) {
      if (!face.vertices3d || !faceBasis(face.vertices3d) || !Number.isFinite(distanceMm) || distanceMm === 0) return false;
      commit(set, get, 'Push/Pull face', (project) => ({ ...project, drawings: project.drawings.map((drawing) => drawing.id === face.id ? { ...drawing, extrusionMm: (drawing.extrusionMm ?? 0) + distanceMm } : drawing) }));
      return true;
    }
    return false;
  },
  pushPullDrawingFace: (drawingId, surface, distanceMm) => {
    const face = get().project.drawings.find((drawing) => drawing.id === drawingId && drawing.kind === 'face');
    const replacement = face && pushPullFaceSurface(face, surface, distanceMm);
    if (!replacement) return false;
    commit(set, get, 'Push/Pull face', (project) => ({ ...project, drawings: project.drawings.map((drawing) => drawing.id === drawingId ? replacement : drawing) }));
    return true;
  },
  moveSelection: (delta) => {
    if (delta.some((value) => !Number.isFinite(value)) || Math.hypot(...delta) < .001) return false;
    const furnitureIds = new Set(get().selectedIds);
    const drawingIds = new Set(get().selectedDrawingIds);
    if (!furnitureIds.size && !drawingIds.size) return false;
    commit(set, get, 'Move selection', (project) => ({ ...project,
      furniture: project.furniture.map((item) => furnitureIds.has(item.id) ? { ...item, position: item.position.map((value, axis) => value + delta[axis]) as Vec3 } : item),
      drawings: project.drawings.map((drawing) => {
        if (!drawingIds.has(drawing.id)) return drawing;
        const points3d = drawing.points3d?.map((p) => p.map((value, axis) => value + delta[axis]) as Vec3) as [Vec3, Vec3] | undefined;
        const vertices3d = drawing.vertices3d?.map((p) => p.map((value, axis) => value + delta[axis]) as Vec3);
        return { ...drawing, points3d, vertices3d, points: drawing.points.map(([x, z]) => [x + delta[0], z + delta[2]]) };
      }),
    }));
    return true;
  },
  mirrorSelected: (axis) => {
    const furnitureId = get().selectedId, drawingId = get().selectedDrawingId;
    if (furnitureId) {
      const item = get().project.furniture.find((entry) => entry.id === furnitureId);
      if (!item) return false;
      get().updateFurniture(furnitureId, axis === 'x' ? { mirrorX: !item.mirrorX } : { mirrorZ: !item.mirrorZ }, 'Mirror object');
      return true;
    }
    if (!drawingId) return false;
    commit(set, get, 'Mirror drawing', (project) => ({ ...project, drawings: project.drawings.map((drawing) => drawing.id === drawingId ? mirrorDrawing(drawing, axis) : drawing) }));
    return true;
  },
  offsetSelectedLine: (distanceMm) => {
    const drawing = get().project.drawings.find((entry) => entry.id === get().selectedDrawingId);
    if (!drawing) return false;
    const endpoints = offsetLine(drawing, distanceMm);
    if (!endpoints) return false;
    get().addLine3D(...endpoints);
    return true;
  },
  groupFurniture: (ids, name) => {
    const unique = [...new Set(ids)];
    const members = get().project.furniture.filter((item) => unique.includes(item.id) && !item.groupId);
    if (members.length < 2 || members.length !== unique.length || !name.trim()) return false;
    const groupId = createId('group');
    commit(set, get, 'Group objects', (project) => ({ ...project, groups: [...(project.groups ?? []), { id: groupId, name: name.trim() }], furniture: project.furniture.map((item) => unique.includes(item.id) ? { ...item, groupId } : item) }));
    set({ selectedGroupId: groupId, selectedId: members[0].id, selectedDrawingId: null, selectedIds: members.map((item) => item.id), selectedDrawingIds: [], selectedPanelId: null });
    return true;
  },
  ungroupSelected: () => {
    const groupId = get().selectedGroupId;
    if (!groupId) return;
    commit(set, get, 'Ungroup objects', (project) => ({ ...project, groups: (project.groups ?? []).filter((group) => group.id !== groupId), furniture: project.furniture.map((item) => item.groupId === groupId ? { ...item, groupId: undefined } : item) }));
    set({ selectedGroupId: null });
  },
  deleteSelected: () => {
    const ids = new Set(get().selectedIds.length ? get().selectedIds : get().selectedId ? [get().selectedId!] : []);
    const drawingIds = new Set(get().selectedDrawingIds.length ? get().selectedDrawingIds : get().selectedDrawingId ? [get().selectedDrawingId!] : []);
    const groupId = get().selectedGroupId;
    if (!ids.size && !drawingIds.size) return;
    commit(set, get, groupId ? 'Delete group' : 'Delete selection', (project) => ({
      ...project,
      groups: groupId ? (project.groups ?? []).filter((group) => group.id !== groupId) : project.groups,
      furniture: project.furniture.filter((item) => !ids.has(item.id) && (!groupId || item.groupId !== groupId)),
      drawings: project.drawings.filter((item) => !drawingIds.has(item.id)),
    }));
    set({ selectedId: null, selectedDrawingId: null, selectedIds: [], selectedDrawingIds: [], selectedGroupId: null, selectedPanelId: null });
  },
  addDrawing: (kind, points) => {
    let facePoints: Array<[number, number]> | null = null;
    if (kind === 'rectangle' && points.length === 2 && Math.abs(points[1][0] - points[0][0]) > .001 && Math.abs(points[1][1] - points[0][1]) > .001) facePoints = [points[0], [points[1][0], points[0][1]], points[1], [points[0][0], points[1][1]]];
    if (kind === 'circle' && points.length === 2) { const radius = Math.hypot(points[1][0] - points[0][0], points[1][1] - points[0][1]); if (radius > .001) facePoints = Array.from({ length: 48 }, (_, index) => [points[0][0] + Math.cos(index * Math.PI / 24) * radius, points[0][1] + Math.sin(index * Math.PI / 24) * radius]); }
    if (kind === 'polyline' && points.length >= 4 && Math.hypot(points[0][0] - points.at(-1)![0], points[0][1] - points.at(-1)![1]) < .01) facePoints = points.slice(0, -1);
    const vertices3d = facePoints?.map(([x, z]) => [x, 0, z] as Vec3);
    const validFace = vertices3d && faceBasis(vertices3d);
    const drawing: DrawingEntity = validFace ? { id: createId('face'), kind: 'face', points: facePoints!, vertices3d, extrusionMm: 0, stroke: '#dbe6df' } : { id: createId(kind), kind, points, stroke: '#dbe6df' };
    commit(set, get, `Draw ${kind}`, (project) => ({ ...project, drawings: [...project.drawings, drawing] }));
    set({ selectedId: null, selectedDrawingId: drawing.id, selectedIds: [], selectedDrawingIds: [drawing.id], selectedGroupId: null, selectedPanelId: null });
  },
  addPolyline3D: (vertices) => {
    if (vertices.length < 2) return;
    const closed = vertices.length >= 4 && Math.hypot(...vertices[0].map((value, index) => value - vertices.at(-1)![index]) as Vec3) < .01;
    const boundary = closed ? vertices.slice(0, -1) : vertices;
    const isFace = closed && !!faceBasis(boundary);
    const drawing: DrawingEntity = { id: createId(isFace ? 'face' : 'polyline'), kind: isFace ? 'face' : 'polyline', points: boundary.map(([x, , z]) => [x, z]), vertices3d: boundary.map((point) => [...point] as Vec3), ...(isFace ? { extrusionMm: 0 } : {}), stroke: '#dbe6df' };
    commit(set, get, isFace ? 'Create face' : 'Draw 3D polyline', (project) => ({ ...project, drawings: [...project.drawings, drawing] }));
    set({ selectedId: null, selectedDrawingId: drawing.id, selectedIds: [], selectedDrawingIds: [drawing.id], selectedGroupId: null, selectedPanelId: null });
  },
  addLine3D: (start, end) => {
    const endpoints: [Vec3, Vec3] = [[...start], [...end]];
    if (!validLine(endpoints)) return;
    const drawing: DrawingEntity = { id: createId('line'), kind: 'line', points: linePlanPoints(endpoints), points3d: endpoints, stroke: '#dbe6df' };
    const closed = closedFaceFromLines(get().project.drawings, start, end);
    if (closed) {
      const face: DrawingEntity = { id: createId('face'), kind: 'face', points: closed.vertices.map(([x, , z]) => [x, z]), vertices3d: closed.vertices, extrusionMm: 0, stroke: '#dbe6df' };
      commit(set, get, 'Create face', (project) => ({ ...project, drawings: [...project.drawings.filter((item) => !closed.consumedIds.includes(item.id)), face] }));
      set({ selectedId: null, selectedDrawingId: face.id, selectedIds: [], selectedDrawingIds: [face.id], selectedGroupId: null, selectedPanelId: null });
    } else {
      commit(set, get, 'Draw 3D line', (project) => ({ ...project, drawings: [...project.drawings, drawing] }));
      set({ selectedId: null, selectedDrawingId: drawing.id, selectedIds: [], selectedDrawingIds: [drawing.id], selectedGroupId: null, selectedPanelId: null });
    }
  },
  updateLine3D: (id, start, end) => {
    const endpoints: [Vec3, Vec3] = [[...start], [...end]];
    if (!validLine(endpoints)) return;
    commit(set, get, 'Edit 3D line', (project) => ({ ...project, drawings: project.drawings.map((drawing) => drawing.id === id && drawing.kind === 'line' ? { ...drawing, points: linePlanPoints(endpoints), points3d: endpoints } : drawing) }));
  },
  selectDrawing: (selectedDrawingId) => set({ selectedDrawingId, selectedId: null, selectedIds: [], selectedDrawingIds: selectedDrawingId ? [selectedDrawingId] : [], selectedGroupId: null, selectedPanelId: null }),
  selectMany: (furnitureIds, drawingIds) => {
    const selectedIds = [...new Set(furnitureIds)].filter((id) => get().project.furniture.some((item) => item.id === id && item.visible));
    const selectedDrawingIds = [...new Set(drawingIds)].filter((id) => get().project.drawings.some((item) => item.id === id));
    set({ selectedIds, selectedDrawingIds, selectedId: selectedIds[0] ?? null, selectedDrawingId: selectedDrawingIds[0] ?? null, selectedGroupId: null, selectedPanelId: null });
  },
  selectGroup: (selectedGroupId) => {
    const first = get().project.furniture.find((item) => item.groupId === selectedGroupId);
    set({ selectedGroupId, selectedId: first?.id ?? null, selectedDrawingId: null, selectedIds: first ? [first.id] : [], selectedDrawingIds: [], selectedPanelId: null });
  },
  updateRoom: (patch) => commit(set, get, 'Edit room', (project) => ({ ...project, room: roomWithPatch(project.room, patch) })),
  deleteRoom: () => commit(set, get, 'Delete room', (project) => ({ ...project, room: { ...project.room, visible: false } })),
  restoreRoom: () => commit(set, get, 'Restore room', (project) => ({ ...project, room: { ...project.room, visible: true } })),
  updateOrigin: (origin) => commit(set, get, 'Move origin', (project) => ({ ...project, origin })),
  select: (selectedId) => set({ selectedId, selectedDrawingId: null, selectedIds: selectedId ? [selectedId] : [], selectedDrawingIds: [], selectedGroupId: get().project.furniture.find((item) => item.id === selectedId)?.groupId ?? null, selectedPanelId: null }),
  selectPanel: (selectedId, selectedPanelId) => set({ selectedId, selectedDrawingId: null, selectedIds: [selectedId], selectedDrawingIds: [], selectedGroupId: null, selectedPanelId }),
  setMode: (mode) => set({ mode }),
  setCameraView: (cameraView) => set({ cameraView }),
  toggleGrid: () => commit(set, get, 'Toggle grid', (project) => ({ ...project, settings: { ...project.settings, gridVisible: !project.settings.gridVisible } })),
  toggleSnap: () => commit(set, get, 'Toggle snap', (project) => ({ ...project, settings: { ...project.settings, snapEnabled: !project.settings.snapEnabled } })),
  toggleOrtho: () => commit(set, get, 'Toggle ortho', (project) => ({ ...project, settings: { ...project.settings, orthoEnabled: !project.settings.orthoEnabled } })),
  toggleVisibility: (id) => {
    const item = get().project.furniture.find((entry) => entry.id === id);
    if (item) get().updateFurniture(id, { visible: !item.visible }, 'Toggle visibility');
  },
  nudgeSelected: (axis, delta) => {
    const item = get().project.furniture.find((entry) => entry.id === get().selectedId);
    if (!item) return;
    const position = [...item.position] as Vec3;
    position[axis] += delta;
    get().updateFurniture(item.id, { position }, 'Move object');
  },
  undo: () => {
    const history = [...get().history];
    const command = history.pop();
    if (!command) return;
    const project = command.undo(get().project);
    const selectedId = project.furniture.some((item) => item.id === get().selectedId) ? get().selectedId : null;
    const selectedDrawingId = project.drawings.some((item) => item.id === get().selectedDrawingId) ? get().selectedDrawingId : null;
    set({ project, selectedId, selectedDrawingId, selectedIds: get().selectedIds.filter((id) => project.furniture.some((item) => item.id === id)), selectedDrawingIds: get().selectedDrawingIds.filter((id) => project.drawings.some((item) => item.id === id)), selectedGroupId: (project.groups ?? []).some((group) => group.id === get().selectedGroupId) ? get().selectedGroupId : null, selectedPanelId: selectedId ? get().selectedPanelId : null, history, future: [command, ...get().future], saveStatus: 'unsaved' });
  },
  redo: () => {
    const [command, ...future] = get().future;
    if (!command) return;
    const project = command.execute(get().project);
    const selectedId = project.furniture.some((item) => item.id === get().selectedId) ? get().selectedId : null;
    const selectedDrawingId = project.drawings.some((item) => item.id === get().selectedDrawingId) ? get().selectedDrawingId : null;
    set({ project, selectedId, selectedDrawingId, selectedIds: get().selectedIds.filter((id) => project.furniture.some((item) => item.id === id)), selectedDrawingIds: get().selectedDrawingIds.filter((id) => project.drawings.some((item) => item.id === id)), selectedGroupId: (project.groups ?? []).some((group) => group.id === get().selectedGroupId) ? get().selectedGroupId : null, selectedPanelId: selectedId ? get().selectedPanelId : null, history: [...get().history, command], future, saveStatus: 'unsaved' });
  },
  focusSelected: () => set({ focusNonce: get().focusNonce + 1 }),
  setTransformMode: (transformMode) => set({ transformMode }),
  setActiveTool: (activeTool) => set({
    activeTool,
    transformMode: activeTool === 'move' ? 'translate' : activeTool === 'rotate' ? 'rotate' : activeTool === 'scale' ? 'scale' : 'select',
    ...(['line', 'polyline', 'rectangle', 'circle', 'arc', 'polygon', 'freehand', 'measure', 'dimension'].includes(activeTool) ? { selectedId: null, selectedIds: [], selectedPanelId: null, selectedDrawingId: null, selectedDrawingIds: [] } : {}),
  }),
  setFullscreenViewport: (fullscreenViewport) => set({ fullscreenViewport }),
  duplicateSelected: () => {
    const source = get().project.furniture.find((item) => item.id === get().selectedId);
    const groupId = get().selectedGroupId;
    if (groupId && source?.groupId === groupId) {
      const newGroupId = createId('group');
      const group = (get().project.groups ?? []).find((entry) => entry.id === groupId);
      const copies = get().project.furniture.filter((item) => item.groupId === groupId).map((item) => {
        const copy = structuredClone(item);
        const oldId = copy.id;
        copy.id = createId(item.kind);
        copy.groupId = newGroupId;
        copy.name = `${copy.name} copy`;
        copy.position = [copy.position[0] + 150, copy.position[1], copy.position[2] + 150];
        copy.panelOverrides = Object.fromEntries(Object.entries(copy.panelOverrides).map(([panelId, override]) => [panelId.replace(oldId, copy.id), override]));
        return copy;
      });
      commit(set, get, 'Duplicate group', (project) => ({ ...project, groups: [...(project.groups ?? []), { id: newGroupId, name: `${group?.name ?? 'Group'} copy` }], furniture: [...project.furniture, ...copies] }));
      set({ selectedGroupId: newGroupId, selectedId: copies[0].id, selectedIds: copies.map((item) => item.id), selectedDrawingId: null, selectedDrawingIds: [], selectedPanelId: null });
      return;
    }
    const furnitureSources = get().project.furniture.filter((item) => get().selectedIds.includes(item.id));
    const drawingSources = get().project.drawings.filter((item) => get().selectedDrawingIds.includes(item.id));
    if (furnitureSources.length + drawingSources.length > 1 || (!source && drawingSources.length)) {
      const furnitureCopies = furnitureSources.map((item) => {
        const copy = structuredClone(item), oldId = copy.id;
        copy.id = createId(item.kind); copy.groupId = undefined; copy.name = `${copy.name} copy`;
        copy.position = [copy.position[0] + 150, copy.position[1], copy.position[2] + 150];
        copy.panelOverrides = Object.fromEntries(Object.entries(copy.panelOverrides).map(([panelId, override]) => [panelId.replace(oldId, copy.id), override]));
        return copy;
      });
      const drawingCopies = drawingSources.map((item) => ({ ...structuredClone(item), id: createId(item.kind), groupId: undefined,
        points: item.points.map(([x, z]) => [x + 150, z + 150] as [number, number]),
        points3d: item.points3d?.map(([x, y, z]) => [x + 150, y, z + 150] as Vec3) as [Vec3, Vec3] | undefined,
        vertices3d: item.vertices3d?.map(([x, y, z]) => [x + 150, y, z + 150] as Vec3),
      }));
      commit(set, get, 'Duplicate selection', (project) => ({ ...project, furniture: [...project.furniture, ...furnitureCopies], drawings: [...project.drawings, ...drawingCopies] }));
      set({ selectedId: furnitureCopies[0]?.id ?? null, selectedIds: furnitureCopies.map((item) => item.id), selectedDrawingId: drawingCopies[0]?.id ?? null, selectedDrawingIds: drawingCopies.map((item) => item.id), selectedGroupId: null, selectedPanelId: null });
      return;
    }
    if (!source) return;
    const copy = structuredClone(source);
    copy.id = `${source.kind}_${crypto.randomUUID()}`;
    copy.groupId = undefined;
    copy.name = `${source.name} copy`;
    copy.position = [source.position[0] + 150, source.position[1], source.position[2] + 150];
    copy.panelOverrides = Object.fromEntries(Object.entries(source.panelOverrides).map(([panelId, override]) => [panelId.replace(source.id, copy.id), override]));
    commit(set, get, 'Duplicate object', (project) => ({ ...project, furniture: [...project.furniture, copy] }));
    set({ selectedId: copy.id, selectedIds: [copy.id], selectedDrawingId: null, selectedDrawingIds: [], selectedPanelId: null });
  },
  setPanelMaterial: (furnitureId, panelId, override) => commit(set, get, 'Change panel material', (project) => ({
    ...project,
    furniture: project.furniture.map((item) => item.id === furnitureId ? { ...item, panelOverrides: { ...item.panelOverrides, [panelId]: override } } : item),
  })),
  addMaterial: (material) => commit(set, get, 'Add material', (project) => ({ ...project, materials: [...project.materials, material] })),
  markSaving: () => set({ saveStatus: 'saving' }),
  markSaved: (project) => set({ saveStatus: 'saved', ...(project ? { project } : {}) }),
  markOffline: () => set({ saveStatus: 'offline' }),
}));
